class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.body = document.querySelector('body');
    this.activeClass = window.drawerToggleClasses.cartDrawer;
    this.cartIconBubble = document.querySelectorAll('[aria-controls="cartDrawer"]');

    this.options = {
      showCartDrawerUpsell: false,
    };

    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    this.addEventListener('cart-drawer:render-contents', this.renderContents.bind(this));
  }

  /**
   * called after an item is added to the cart from the product-form-component.js and the settings say to open the drawer
   * @param {Object} trigger, is passed on from product-form-component, in case there was an add from quickshop
   */
  open(trigger = null) {
    const drawer = this.querySelector('#cartDrawer');
    this.body.classList.add(this.activeClass);
    this.cartIconBubble.forEach(button => button.setAttribute('aria-expanded', true));

    // add the eventListener to close on ESC key
    drawer.addEventListener('keyup', event => event.code === 'Escape' && this.close(trigger));

    scrollLock.lock();
    trapFocus(drawer);
  }

  /**
   * called after an item is added to the cart from the product-form-component.js and the ESC key was used to close the drawer
   * @param {Object} trigger, is passed on from product-form-component, in case there was an add from quickshop
   */
  close(trigger = null) {
    this.body.classList.remove(this.activeClass);
    this.cartIconBubble.forEach(button => button.setAttribute('aria-expanded', false));

    scrollLock.unlock();
    removeTrapFocus(trigger);
  }

  /**
   * renderContents:
   * @param {Object} parsedState, passed after an item is added to the cart from the product-form-component.js
   */
  renderContents(event) {
    const parsedState = (event.detail) ? event.detail : event;
    if (!parsedState.sections) return;

    this.getSectionsToRender().forEach((section, index) => {
      if (section?.selector) {
        const selector = document.querySelector(section.selector);

        if (selector) {
          selector.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }
      }

      if (index === this.getSectionsToRender().length - 1) {
        setTimeout(() => {
          window.initCartCheckoutButtons();
          window.initDialogs(document.querySelector('cart-drawer'));
        }, 200);
      }
    });
  }

  /**
   * renderCartDrawer
   * Renders the drawer and the cart icon bubble
   * When there is an error for the quantity that is being added, the max quantity still gets added, so we have to update the sections.
   */
  async renderCartDrawer() {
    const body = JSON.stringify( {
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: window.location.pathname,
    });
    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig('javascript'),
      ...{ body },
    });

    const parsedState = await response.json();

    if (!parsedState.sections) {
      const sectionIds = this.getSectionsToRender().map(section => section.section);
      const sectionsResponse = await fetch(`/?sections=${sectionIds.join(',')}`);
      parsedState.sections = await sectionsResponse.json();
    }

    this.renderContents(parsedState);
  }

  /**
   * getSectionsToRender:
   * @returns the sections to be rendered after an item is added to the cart from the product-form-component.js.
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '[data-cart-drawer]',
      },
      {
        id: 'cart-icon-bubble',
        section: 'theme-cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDiscountComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartDiscountError = this.querySelector('[ref="cartDiscountError"]');
    this.cartDiscountErrorDiscountCode = this.querySelector('[ref="cartDiscountErrorDiscountCode"]');
    this.cartDiscountErrorShipping = this.querySelector('[ref="cartDiscountErrorShipping"]');

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.updateDiscounts.bind(this));
    this.querySelectorAll('[data-discount-remove]').forEach(button => button.addEventListener('click', this.updateDiscounts.bind(this)));
  }

  disconnectedCallback() {
    this.querySelector('form').removeEventListener('submit', this.updateDiscounts.bind(this));
  }

  updateDiscounts = async event => {
    event.preventDefault();
    event.stopPropagation();

    const existingDiscounts = this.options.existingDiscounts;
    const type = ('discountRemove' in event.currentTarget.dataset) ? 'discount_remove' : 'discount_add';
    const discountCodeValue = type === 'discount_add' ? this.form.querySelector('input[name="discount"]').value : event.currentTarget.dataset.discountRemove;

    if (type === 'discount_remove') {
      const index = existingDiscounts.indexOf(discountCodeValue.toLowerCase());
      if (index === -1) return;

      existingDiscounts.splice(index, 1);
    }
    else if (type === 'discount_add' && existingDiscounts.includes(discountCodeValue.toLowerCase())) return;
    else if (type === 'discount_add') {
      this.form.querySelector('[type="submit"]').classList.add('button--loading');
      this.form.querySelector('[type="submit"]').setAttribute('disabled', true);
    }

    try {
      const discounts = type === 'discount_add' ? [...existingDiscounts, discountCodeValue] : existingDiscounts;

      this.cartDiscountError.classList.add('hidden');
      this.cartDiscountErrorDiscountCode.classList.replace('flex', 'hidden');
      this.cartDiscountErrorShipping.classList.replace('flex', 'hidden');

      const body = JSON.stringify( {
        discount: discounts.join(','),
        sections: this.cartDrawer.getSectionsToRender().map(section => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig('javascript'),
        ...{ body },
      });

      const data = await response.json();

      // if the discount is not applicable, show an error text
      if (data.discount_codes.find(discount => discount.code === discountCodeValue && discount.applicable === false)) {
        this.#handleDiscountError('discount_code');
        return;
      }
      else {
        // if the discount isn't added to the discount codes, it's a shipping discount and we need to show a correct text
        const newHtml = data.sections['cart-drawer'];
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.querySelector('cart-drawer');
        const discountCodes = section?.querySelectorAll('[data-discount-remove]') || [];

        if (section) {
          const codes = Array.from(discountCodes)
            .map(element => (element.dataset.discountRemove))
            .filter(Boolean);
          if (
            codes.length === existingDiscounts.length &&
            codes.every(code => existingDiscounts.includes(code)) &&
            data.discount_codes.find(discount => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            return;
          }
        }

        this.cartDrawer.dispatchEvent(new CustomEvent('cart-drawer:render-contents', { bubbles: true,
          detail: data }));
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      if (type === 'discount_add') {
        this.form.querySelector('[type="submit"]').classList.remove('button--loading');
        this.form.querySelector('[type="submit"]').removeAttribute('disabled');
      }
    }
  };

  #handleDiscountError(type) {
    const target = type === 'discount_code' ? this.cartDiscountErrorDiscountCode : this.cartDiscountErrorShipping;
    this.cartDiscountError.classList.remove('hidden');
    target.classList.replace('hidden', 'flex');
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscountComponent);
}